import PropTypes from "prop-types";
import { PureComponent } from "react";

import { defineMessages, FormattedMessage } from "react-intl";

import classNames from "classnames";
import { Redirect, Route, withRouter } from "react-router-dom";

import { connect } from "react-redux";

import Favico from "favico.js";
import { debounce } from "lodash";

import { scrollRight } from "../../scroll";
import { focusApp, unfocusApp, changeLayout } from "flavours/glitch/actions/app";
import {
  synchronouslySubmitMarkers,
  submitMarkers,
  fetchMarkers,
} from "flavours/glitch/actions/markers";
import { fetchNotifications } from "flavours/glitch/actions/notification_groups";
import { INTRODUCTION_VERSION } from "flavours/glitch/actions/onboarding";
import { AlertsController } from "flavours/glitch/components/alerts_controller";
import { injectIntl } from "@/flavours/glitch/components/intl";
import { Hotkeys } from "flavours/glitch/components/hotkeys";
import { HoverCardController } from "flavours/glitch/components/hover_card_controller";
import { Permalink } from "flavours/glitch/components/permalink";
import { PictureInPicture } from "flavours/glitch/features/picture_in_picture";
import { identityContextPropShape, withIdentity } from "flavours/glitch/identity_context";
import { layoutFromWindow } from "flavours/glitch/is_mobile";
import { selectUnreadNotificationGroupsCount } from "flavours/glitch/selectors/notifications";
import { WithRouterPropTypes } from "flavours/glitch/utils/react_router";
import { checkAnnualReport } from "@/flavours/glitch/reducers/slices/annual_report";
import { checkWelcomeDigest } from "@/flavours/glitch/reducers/slices/welcome_digest";

import { uploadCompose, resetCompose, changeComposeSpoilerness } from "../../actions/compose";
import { clearHeight } from "../../actions/height_cache";
import { fetchServer, fetchServerTranslationLanguages } from "../../actions/server";
import { expandHomeTimeline } from "../../actions/timelines";
import {
  initialState,
  me,
  owner,
  singleUserMode,
  trendsEnabled,
  landingPage,
  localLiveFeedAccess,
  disableHoverCards,
  domain,
} from "../../initial_state";

import BundleColumnError from "./components/bundle_column_error";
import { InstallAppBanner } from "./components/install_app_banner";
import { ServiceWorkerUpdateNotice } from "./components/service_worker_update_notice";
import { NavigationBar } from "./components/navigation_bar";
import { NavPanelFab } from "./components/nav_panel_fab";
import { UploadArea } from "./components/upload_area";
import { HashtagMenuController } from "./components/hashtag_menu_controller";
import { ColumnsArea } from "./components/columns_area";
import LoadingBarContainer from "./containers/loading_bar_container";
import ModalContainer from "./containers/modal_container";
import {
  Compose,
  Status,
  GettingStarted,
  KeyboardShortcuts,
  Firehose,
  AccountTimeline,
  AccountGallery,
  HomeTimeline,
  Followers,
  Following,
  Reblogs,
  Favourites,
  DirectTimeline,
  HashtagTimeline,
  Notifications,
  NotificationRequests,
  NotificationRequest,
  FollowRequests,
  FavouritedStatuses,
  BookmarkedStatuses,
  FollowedTags,
  LinkTimeline,
  ListTimeline,
  Lists,
  ListEdit,
  ListMembers,
  Collections,
  CollectionDetail,
  CollectionsEditor,
  Blocks,
  DomainBlocks,
  Mutes,
  PinnedStatuses,
  Directory,
  CommunityDirectoryAdmin,
  OnboardingProfile,
  OnboardingFollows,
  Explore,
  Search,
  PrivacyPolicy,
  TermsOfService,
  AccountFeatured,
  AccountEdit,
  AccountEditFeaturedTags,
  Quotes,
  CommunityMaintenance,
  CommunityDirectory,
  CommunityDirectoryFormBuilder,
  CommunityDirectoryEdit,
  CommunityDirectoryModeration,
  CommunityDirectoryPermissions,
  CommunityDirectoryScraperLogs,
  CommunityDirectoryLocations,
  CommunityArtists,
  CommunityArtistsShow,
  CommunityArtistsNew,
  CommunityArtistsEdit,
  CommunityEvents,
  CommunityEventsShow,
  CommunityEventsNew,
  CommunityEventsEdit,
  CommunityVisits,
  CommunityVisitsNotifications,
  CommunityVisitsAdmin,
  CommunityListings,
  CommunityListingsShow,
  CommunityListingsNew,
  CommunityListingsEdit,
  CommunityLanding,
  CommunityLandingSettings,
  CommunityServices,
  CommunityServicesShow,
  CommunityServicesNew,
  CommunityServicesEdit,
  CommunityRestaurants,
  CommunityRestaurantsShow,
  CommunityRestaurantsNew,
  CommunityRestaurantsEdit,
  CommunityProperties,
  CommunityPropertiesShow,
  CommunityPropertiesNew,
  CommunityPropertiesEdit,
  CommunityDirectoryEntries,
  MemberStories,
  MemberStoriesShow,
  MemberStoriesEdit,
  ContactPage,
  DailyDigest,
  NewsletterIndex,
  NewsletterShow,
  QuickShareIndex,
  QuickShareNew,
  QuickShareShow,
  NotificationSettings,
  CommunityEntryNotifications,
  HowItWorks,
  // [CD:ASYNC_IMPORTS]
} from "./util/async-components";
import { ColumnsContextProvider } from "./util/columns_context";
import {
  focusColumn,
  getFocusedItemIndex,
  focusItemSibling,
  focusFirstItem,
  getFocusedColumnIndex,
} from "./util/focusUtils";
import { WrappedSwitch, WrappedRoute } from "./util/react_router_helpers";
import { CustomHomepage } from "flavours/glitch/features/custom_homepage";

// Dummy import, to make sure that <Status /> ends up in the application bundle.
// Without this it ends up in ~8 very commonly used bundles.
import "../../components/status";
import { getNavigationSkipLinkId, SkipLinks } from "./components/skip_links";

const messages = defineMessages({
  beforeUnload: {
    id: "ui.beforeunload",
    defaultMessage: "Your draft will be lost if you leave Mastodon.",
  },
});

const mapStateToProps = (state) => ({
  layout: state.getIn(["meta", "layout"]),
  hasComposingContents:
    state.getIn(["compose", "text"]).trim().length !== 0 ||
    state.getIn(["compose", "media_attachments"]).size > 0 ||
    state.getIn(["compose", "poll"]) !== null ||
    state.getIn(["compose", "quoted_status_id"]) !== null,
  canUploadMore:
    !state
      .getIn(["compose", "media_attachments"])
      .some((x) => ["audio", "video"].includes(x.get("type"))) &&
    state.getIn(["compose", "media_attachments"]).size <
      state.getIn([
        "server",
        "server",
        "item",
        "configuration",
        "statuses",
        "max_media_attachments",
      ]),
  isUploadEnabled: state.getIn(["compose", "isDragDisabled"]) !== true,
  isWide: state.getIn(["local_settings", "stretch"]),
  fullWidthColumns: state.getIn(["local_settings", "fullwidth_columns"]),
  unreadNotifications: selectUnreadNotificationGroupsCount(state),
  showFaviconBadge: state.getIn(["local_settings", "notifications", "favicon_badge"]),
  hicolorPrivacyIcons: state.getIn(["local_settings", "hicolor_privacy_icons"]),
  moved:
    state.getIn(["accounts", me, "moved"]) &&
    state.getIn(["accounts", state.getIn(["accounts", me, "moved"])]),
  firstLaunch: state.getIn(["settings", "introductionVersion"], 0) < INTRODUCTION_VERSION,
  newAccount:
    !state.getIn(["accounts", me, "note"]) &&
    !state.getIn(["accounts", me, "bot"]) &&
    state.getIn(["accounts", me, "following_count"], 0) === 0 &&
    state.getIn(["accounts", me, "statuses_count"], 0) === 0,
  username: state.getIn(["accounts", me, "username"]),
});

class SwitchingColumnsArea extends PureComponent {
  static propTypes = {
    identity: identityContextPropShape,
    children: PropTypes.node,
    location: PropTypes.object,
    singleColumn: PropTypes.bool,
    layout: PropTypes.string.isRequired,
    forceOnboarding: PropTypes.bool,
  };

  componentDidMount() {
    document.body.classList.toggle("layout-single-column", this.props.singleColumn);
    document.body.classList.toggle("layout-multiple-columns", !this.props.singleColumn);
  }

  componentDidUpdate(prevProps) {
    if (![this.props.location.pathname, "/"].includes(prevProps.location.pathname)) {
      this.handleChildrenContentChange();
    }

    if (prevProps.singleColumn !== this.props.singleColumn) {
      document.body.classList.toggle("layout-single-column", this.props.singleColumn);
      document.body.classList.toggle("layout-multiple-columns", !this.props.singleColumn);
    }
  }

  handleChildrenContentChange() {
    const { preventMultiColumnAutoScroll } = this.props.location.state ?? {};

    if (!this.props.singleColumn && !preventMultiColumnAutoScroll) {
      const isRtlLayout = document.getElementsByTagName("body")[0]?.classList.contains("rtl");
      const modifier = isRtlLayout ? -1 : 1;

      scrollRight(this.node, (this.node.scrollWidth - window.innerWidth) * modifier);
    }
  }

  setRef = (c) => {
    if (c) {
      this.node = c;
    }
  };

  render() {
    const { children, singleColumn, forceOnboarding } = this.props;
    const { signedIn } = this.props.identity;
    const pathName = this.props.location.pathname;

    let rootRedirect;
    if (signedIn && forceOnboarding) {
      rootRedirect = "/start";
    } else if (singleUserMode && owner && initialState?.accounts[owner]) {
      rootRedirect = `/@${initialState.accounts[owner].username}`;
    } else if (singleColumn && !signedIn) {
      // Reads the real "Index page" admin setting (Admin > Server Settings >
      // Branding) instead of a hardcoded path, so it's admin-editable and
      // persisted through Mastodon's own Setting.landing_page, not just
      // hardcoded here. 'about' and any unrecognized/default value (the
      // actual default is 'guide') fall through to /landing -- the
      // consolidated Live Posts / Daily Digest / Community front door --
      // instead of the bare /guide explainer page. /guide itself is
      // unchanged and still reachable from /landing and the nav panel.
      if (trendsEnabled && landingPage === "trends") {
        rootRedirect = "/explore";
      } else if (localLiveFeedAccess === "public" && landingPage === "local_feed") {
        rootRedirect = "/public/local";
      } else {
        rootRedirect = "/landing";
      }
    }

    return (
      <ColumnsContextProvider multiColumn={!singleColumn}>
        <ColumnsArea
          ref={this.setRef}
          singleColumn={singleColumn}
          domain={domain}
          minimalShell={!signedIn && landingPage === "overview" && pathName.startsWith("/overview")}
        >
          <WrappedSwitch>
            {rootRedirect ? (
              <Redirect
                from="/"
                to={{
                  pathname: rootRedirect,
                  state: { ...this.props.location.state, focusTarget: false },
                }}
                exact
              />
            ) : (
              <WrappedRoute path="/" exact component={CommunityLanding} content={children} />
            )}

            {singleColumn ? <Redirect from="/deck" to="/home" exact /> : null}
            {singleColumn && pathName.startsWith("/deck/") ? (
              <Redirect
                from={pathName}
                to={{ ...this.props.location, pathname: pathName.slice(5) }}
              />
            ) : null}
            {/* Redirect old bookmarks (without /deck) with home-like routes to the advanced interface */}
            {!singleColumn && pathName === "/home" ? (
              <Redirect from="/home" to="/deck/getting-started" exact />
            ) : null}
            {pathName === "/getting-started" ? (
              <Redirect
                from="/getting-started"
                to={singleColumn ? "/home" : "/deck/getting-started"}
                exact
              />
            ) : null}

            <WrappedRoute path="/getting-started" component={GettingStarted} content={children} />
            <WrappedRoute
              path="/keyboard-shortcuts"
              component={KeyboardShortcuts}
              content={children}
            />
            <Redirect from="/about" to="/guide" exact />
            <WrappedRoute path="/privacy-policy" component={PrivacyPolicy} content={children} />
            <WrappedRoute
              path="/terms-of-service/:date?"
              component={TermsOfService}
              content={children}
            />

            <WrappedRoute
              path={["/home", "/timelines/home"]}
              component={HomeTimeline}
              content={children}
            />
            <Redirect from="/timelines/public" to="/public" exact />
            <Redirect from="/timelines/public/local" to="/public/local" exact />
            <WrappedRoute
              path="/public"
              exact
              component={Firehose}
              componentParams={{ feedType: "public" }}
              content={children}
            />
            <WrappedRoute
              path="/public/local"
              exact
              component={Firehose}
              componentParams={{ feedType: "community" }}
              content={children}
            />
            <WrappedRoute
              path="/public/remote"
              exact
              component={Firehose}
              componentParams={{ feedType: "public:remote" }}
              content={children}
            />
            <WrappedRoute
              path={["/conversations", "/timelines/direct"]}
              component={DirectTimeline}
              content={children}
            />
            <WrappedRoute path="/tags/:id" component={HashtagTimeline} content={children} />
            <WrappedRoute path="/links/:url" component={LinkTimeline} content={children} />
            <WrappedRoute path="/lists/new" component={ListEdit} content={children} />
            <WrappedRoute path="/lists/:id/edit" component={ListEdit} content={children} />
            <WrappedRoute path="/lists/:id/members" component={ListMembers} content={children} />
            <WrappedRoute path="/lists/:id" component={ListTimeline} content={children} />
            <WrappedRoute
              path="/notifications"
              component={Notifications}
              content={children}
              exact
            />
            <WrappedRoute
              path="/notifications/requests"
              component={NotificationRequests}
              content={children}
              exact
            />
            <WrappedRoute
              path="/notifications/requests/:id"
              component={NotificationRequest}
              content={children}
              exact
            />
            <WrappedRoute path="/favourites" component={FavouritedStatuses} content={children} />

            <WrappedRoute path="/bookmarks" component={BookmarkedStatuses} content={children} />
            <WrappedRoute path="/pinned" component={PinnedStatuses} content={children} />

            <WrappedRoute
              path="/start/profile"
              exact
              component={OnboardingProfile}
              content={children}
            />
            <WrappedRoute
              path={["/start", "/start/follows"]}
              exact
              component={OnboardingFollows}
              content={children}
            />
            <WrappedRoute path="/directory" component={Directory} content={children} />
            {/* Community Directory */}
            {/* /community used to render its own separate, older category-grid
                page (CommunityHub) -- now retired in favor of /landing, the
                consolidated front door, so every path into "browse all
                categories" lands on the same page. Client-side redirect (not
                just a server-side one) so this also fires for in-app link
                clicks, which never leave the SPA router. */}
            <Redirect from="/community" to="/landing" exact />
            <WrappedRoute
              path="/community_maintenance"
              exact
              component={CommunityMaintenance}
              content={children}
            />
            <WrappedRoute
              path="/community_directory/admin"
              component={CommunityDirectoryFormBuilder}
              content={children}
            />
            <WrappedRoute
              path="/community_directory/edit/:category"
              component={CommunityDirectoryEdit}
              content={children}
            />
            <WrappedRoute
              path="/community_directory/scraper_logs"
              exact
              component={CommunityDirectoryScraperLogs}
              content={children}
            />
            <WrappedRoute
              path="/community_directory/moderation"
              exact
              component={CommunityDirectoryModeration}
              content={children}
            />
            <WrappedRoute
              path="/community_directory/permissions"
              exact
              component={CommunityDirectoryPermissions}
              content={children}
            />
            <WrappedRoute
              path="/community_directory/locations"
              exact
              component={CommunityDirectoryLocations}
              content={children}
            />
            <WrappedRoute
              path="/community_directory"
              exact
              component={CommunityDirectory}
              content={children}
            />
            {/* Community Listings */}
            <WrappedRoute
              path="/community_listings/new"
              exact
              component={CommunityListingsNew}
              content={children}
            />
            <WrappedRoute
              path="/community_listings/:id/edit"
              exact
              component={CommunityListingsEdit}
              content={children}
            />
            <WrappedRoute
              path="/community_listings/:id"
              exact
              component={CommunityListingsShow}
              content={children}
            />
            <WrappedRoute
              path="/community_listings"
              exact
              component={CommunityListings}
              content={children}
            />
            {/* Community Visits */}
            <WrappedRoute
              path="/community_visits/admin"
              exact
              component={CommunityVisitsAdmin}
              content={children}
            />
            <WrappedRoute
              path="/community_visits/notifications"
              exact
              component={CommunityVisitsNotifications}
              content={children}
            />
            <WrappedRoute
              path="/community_visits"
              exact
              component={CommunityVisits}
              content={children}
            />
            {/* Community Artists */}
            <WrappedRoute
              path="/community_artists/new"
              exact
              component={CommunityArtistsNew}
              content={children}
            />
            <WrappedRoute
              path="/community_artists/:id/edit"
              exact
              component={CommunityArtistsEdit}
              content={children}
            />
            <WrappedRoute
              path="/community_artists/:id"
              exact
              component={CommunityArtistsShow}
              content={children}
            />
            <WrappedRoute
              path="/community_artists"
              exact
              component={CommunityArtists}
              content={children}
            />
            {/* Community Events */}
            <WrappedRoute
              path="/community_events/new"
              exact
              component={CommunityEventsNew}
              content={children}
            />
            <WrappedRoute
              path="/community_events/:id/edit"
              exact
              component={CommunityEventsEdit}
              content={children}
            />
            <WrappedRoute
              path="/community_events/:id"
              exact
              component={CommunityEventsShow}
              content={children}
            />
            <WrappedRoute
              path="/community_events"
              exact
              component={CommunityEvents}
              content={children}
            />
            {/* Landing page */}
            <WrappedRoute path="/landing" exact component={CommunityLanding} content={children} />
            <WrappedRoute
              path="/community_directory/landing-settings"
              exact
              component={CommunityLandingSettings}
              content={children}
            />
            {/* Community Services */}
            <WrappedRoute
              path="/community_services/new"
              exact
              component={CommunityServicesNew}
              content={children}
            />
            <WrappedRoute
              path="/community_services/:id/edit"
              exact
              component={CommunityServicesEdit}
              content={children}
            />
            <WrappedRoute
              path="/community_services/:id"
              exact
              component={CommunityServicesShow}
              content={children}
            />
            <WrappedRoute
              path="/community_services"
              exact
              component={CommunityServices}
              content={children}
            />
            {/* Community Restaurants */}
            <WrappedRoute
              path="/community_restaurants/new"
              exact
              component={CommunityRestaurantsNew}
              content={children}
            />
            <WrappedRoute
              path="/community_restaurants/:id/edit"
              exact
              component={CommunityRestaurantsEdit}
              content={children}
            />
            <WrappedRoute
              path="/community_restaurants/:id"
              exact
              component={CommunityRestaurantsShow}
              content={children}
            />
            <WrappedRoute
              path="/community_restaurants"
              exact
              component={CommunityRestaurants}
              content={children}
            />
            {/* Community Properties */}
            <WrappedRoute
              path="/community_properties/new"
              exact
              component={CommunityPropertiesNew}
              content={children}
            />
            <WrappedRoute
              path="/community_properties/:id/edit"
              exact
              component={CommunityPropertiesEdit}
              content={children}
            />
            <WrappedRoute
              path="/community_properties/:id"
              exact
              component={CommunityPropertiesShow}
              content={children}
            />
            <WrappedRoute
              path="/community_properties"
              exact
              component={CommunityProperties}
              content={children}
            />
            <WrappedRoute
              path="/community_directory/entries/:categoryKey"
              exact
              component={CommunityDirectoryEntries}
              content={children}
            />
            {/* Member Stories */}
            <WrappedRoute
              path="/member_stories/edit"
              exact
              component={MemberStoriesEdit}
              content={children}
            />
            <WrappedRoute
              path="/member_stories/:account_id"
              exact
              component={MemberStoriesShow}
              content={children}
            />
            <WrappedRoute
              path="/member_stories"
              exact
              component={MemberStories}
              content={children}
            />
            <WrappedRoute path="/contact" exact component={ContactPage} content={children} />
            <WrappedRoute path="/daily" exact component={DailyDigest} content={children} />
            <WrappedRoute
              path="/newsletters/:slug"
              exact
              component={NewsletterShow}
              content={children}
            />
            <WrappedRoute
              path="/newsletters"
              exact
              component={NewsletterIndex}
              content={children}
            />
            <WrappedRoute path="/shared/new" exact component={QuickShareNew} content={children} />
            <WrappedRoute
              path="/shared/:slug"
              exact
              component={QuickShareShow}
              content={children}
            />
            <WrappedRoute path="/shared" exact component={QuickShareIndex} content={children} />
            <WrappedRoute path="/guide" exact component={HowItWorks} content={children} />
            <WrappedRoute
              path="/notification_settings"
              exact
              component={NotificationSettings}
              content={children}
            />
            <WrappedRoute
              path="/community_notifications"
              exact
              component={CommunityEntryNotifications}
              content={children}
            />
            {/* [CD:ROUTES] */}

            <WrappedRoute path="/explore" component={Explore} content={children} />
            <WrappedRoute path="/search" component={Search} content={children} />
            <WrappedRoute
              path={["/publish", "/statuses/new"]}
              component={Compose}
              content={children}
            />

            <WrappedRoute path="/profile/edit" component={AccountEdit} content={children} />
            <WrappedRoute
              path="/profile/featured_tags"
              component={AccountEditFeaturedTags}
              content={children}
            />

            <WrappedRoute
              path={["/@:acct", "/accounts/:id"]}
              exact
              component={AccountTimeline}
              content={children}
            />
            <WrappedRoute
              path={["/@:acct/featured", "/accounts/:id/featured"]}
              component={AccountFeatured}
              content={children}
            />
            <WrappedRoute
              path={["/@:acct/collections"]}
              component={Collections}
              content={children}
              key="collections-list"
            />
            <WrappedRoute
              path={["/collections/new", "/collections/:id/edit"]}
              component={CollectionsEditor}
              content={children}
              key="collections-editor"
            />
            <WrappedRoute
              path="/collections/:id"
              component={CollectionDetail}
              content={children}
              key="collections-detail"
            />
            <WrappedRoute
              path="/@:acct/tagged/:tagged?"
              exact
              component={AccountTimeline}
              content={children}
            />
            <WrappedRoute
              path={["/@:acct/with_replies", "/accounts/:id/with_replies"]}
              component={AccountTimeline}
              content={children}
              componentParams={{ withReplies: true }}
            />
            <WrappedRoute
              path={["/accounts/:id/followers", "/users/:acct/followers", "/@:acct/followers"]}
              component={Followers}
              content={children}
            />
            <WrappedRoute
              path={["/accounts/:id/following", "/users/:acct/following", "/@:acct/following"]}
              component={Following}
              content={children}
            />
            <WrappedRoute
              path={["/@:acct/media", "/accounts/:id/media"]}
              component={AccountGallery}
              content={children}
            />
            <WrappedRoute path="/@:acct/:statusId" exact component={Status} content={children} />
            <WrappedRoute path="/@:acct/:statusId/reblogs" component={Reblogs} content={children} />
            <WrappedRoute
              path="/@:acct/:statusId/favourites"
              component={Favourites}
              content={children}
            />
            <WrappedRoute path="/@:acct/:statusId/quotes" component={Quotes} content={children} />

            {/* Legacy routes, cannot be easily factored with other routes because they share a param name */}
            <WrappedRoute
              path="/timelines/tag/:id"
              component={HashtagTimeline}
              content={children}
            />
            <WrappedRoute path="/timelines/list/:id" component={ListTimeline} content={children} />
            <WrappedRoute path="/statuses/:statusId" exact component={Status} content={children} />
            <WrappedRoute
              path="/statuses/:statusId/reblogs"
              component={Reblogs}
              content={children}
            />
            <WrappedRoute
              path="/statuses/:statusId/favourites"
              component={Favourites}
              content={children}
            />

            <WrappedRoute path="/follow_requests" component={FollowRequests} content={children} />
            <WrappedRoute path="/blocks" component={Blocks} content={children} />
            <WrappedRoute path="/domain_blocks" component={DomainBlocks} content={children} />
            <WrappedRoute path="/followed_tags" component={FollowedTags} content={children} />
            <WrappedRoute path="/mutes" component={Mutes} content={children} />
            <WrappedRoute path="/lists" component={Lists} content={children} />

            <Route path="/overview" component={CustomHomepage} />
            <Route component={BundleColumnError} />
          </WrappedSwitch>
        </ColumnsArea>
      </ColumnsContextProvider>
    );
  }
}

class UI extends PureComponent {
  static propTypes = {
    identity: identityContextPropShape,
    dispatch: PropTypes.func.isRequired,
    children: PropTypes.node,
    isWide: PropTypes.bool,
    fullWidthColumns: PropTypes.bool,
    systemFontUi: PropTypes.bool,
    isComposing: PropTypes.bool,
    hasComposingContents: PropTypes.bool,
    canUploadMore: PropTypes.bool,
    intl: PropTypes.object.isRequired,
    unreadNotifications: PropTypes.number,
    showFaviconBadge: PropTypes.bool,
    hicolorPrivacyIcons: PropTypes.bool,
    moved: PropTypes.map,
    layout: PropTypes.string.isRequired,
    firstLaunch: PropTypes.bool,
    newAccount: PropTypes.bool,
    username: PropTypes.string,
    ...WithRouterPropTypes,
  };

  state = {
    draggingOver: false,
  };

  handleBeforeUnload = (e) => {
    const { intl, dispatch, hasComposingContents } = this.props;

    dispatch(synchronouslySubmitMarkers());

    if (hasComposingContents) {
      // Setting returnValue to any string causes confirmation dialog.
      // Many browsers no longer display this text to users,
      // but we set user-friendly message for other browsers, e.g. Edge.
      e.returnValue = intl.formatMessage(messages.beforeUnload);
    }
  };

  handleVisibilityChange = () => {
    const visibility = !document[this.visibilityHiddenProp];
    if (visibility) {
      this.props.dispatch(focusApp());
      this.props.dispatch(submitMarkers({ immediate: true }));
    } else {
      this.props.dispatch(unfocusApp());
    }
  };

  handleDragEnter = (e) => {
    if (!this.props.isUploadEnabled) {
      return;
    }
    e.preventDefault();

    if (!this.dragTargets) {
      this.dragTargets = [];
    }

    if (this.dragTargets.indexOf(e.target) === -1) {
      this.dragTargets.push(e.target);
    }

    if (
      e.dataTransfer &&
      Array.from(e.dataTransfer.types).includes("Files") &&
      this.props.canUploadMore &&
      this.props.identity.signedIn
    ) {
      this.setState({ draggingOver: true });
    }
  };

  handleDragOver = (e) => {
    if (!this.props.isUploadEnabled) {
      return;
    }
    if (this.dataTransferIsText(e.dataTransfer)) return false;

    e.preventDefault();
    e.stopPropagation();

    try {
      e.dataTransfer.dropEffect = "copy";
    } catch {
      // do nothing
    }

    return false;
  };

  handleDrop = (e) => {
    if (!this.props.isUploadEnabled) {
      return;
    }
    if (this.dataTransferIsText(e.dataTransfer)) return;

    e.preventDefault();

    this.setState({ draggingOver: false });
    this.dragTargets = [];

    if (
      e.dataTransfer &&
      e.dataTransfer.files.length >= 1 &&
      this.props.canUploadMore &&
      this.props.identity.signedIn
    ) {
      this.props.dispatch(uploadCompose(e.dataTransfer.files));
    }
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.dragTargets = this.dragTargets.filter((el) => el !== e.target && this.node.contains(el));

    if (this.dragTargets.length > 0) {
      return;
    }

    this.setState({ draggingOver: false });
  };

  dataTransferIsText = (dataTransfer) => {
    return (
      dataTransfer &&
      Array.from(dataTransfer.types).filter((type) => type === "text/plain").length === 1
    );
  };

  closeUploadModal = () => {
    this.setState({ draggingOver: false });
  };

  handleServiceWorkerPostMessage = ({ data }) => {
    if (data.type === "navigate") {
      this.props.history.push(data.path);
    } else {
      console.warn("Unknown message type:", data.type);
    }
  };

  handleLayoutChange = debounce(
    () => {
      this.props.dispatch(clearHeight()); // The cached heights are no longer accurate, invalidate
    },
    500,
    {
      trailing: true,
    },
  );

  handleResize = () => {
    const layout = layoutFromWindow();

    if (layout !== this.props.layout) {
      this.handleLayoutChange.cancel();
      this.props.dispatch(changeLayout({ layout }));
    } else {
      this.handleLayoutChange();
    }
  };

  handleDonate = () => {
    location.href = "https://joinmastodon.org/sponsors#donate";
  };

  componentDidMount() {
    const { signedIn } = this.props.identity;

    window.addEventListener("beforeunload", this.handleBeforeUnload, false);
    window.addEventListener("resize", this.handleResize, { passive: true });

    document.addEventListener("dragenter", this.handleDragEnter, false);
    document.addEventListener("dragover", this.handleDragOver, false);
    document.addEventListener("drop", this.handleDrop, false);
    document.addEventListener("dragleave", this.handleDragLeave, false);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", this.handleServiceWorkerPostMessage);
    }

    this.favicon = new Favico({ animation: "none" });

    if (signedIn) {
      this.props.dispatch(fetchMarkers());
      this.props.dispatch(expandHomeTimeline());
      this.props.dispatch(fetchNotifications());
      this.props.dispatch(fetchServerTranslationLanguages());
      this.props.dispatch(checkAnnualReport());
      this.props.dispatch(checkWelcomeDigest());

      setTimeout(() => this.props.dispatch(fetchServer()), 3000);
    }

    if (typeof document.hidden !== "undefined") {
      // Opera 12.10 and Firefox 18 and later support
      this.visibilityHiddenProp = "hidden";
      this.visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
      this.visibilityHiddenProp = "msHidden";
      this.visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
      this.visibilityHiddenProp = "webkitHidden";
      this.visibilityChange = "webkitvisibilitychange";
    }

    if (this.visibilityChange !== undefined) {
      document.addEventListener(this.visibilityChange, this.handleVisibilityChange, false);
      this.handleVisibilityChange();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.unreadNotifications !== prevProps.unreadNotifications ||
      this.props.showFaviconBadge !== prevProps.showFaviconBadge
    ) {
      if (this.favicon) {
        try {
          this.favicon.badge(this.props.showFaviconBadge ? this.props.unreadNotifications : 0);
        } catch (err) {
          console.error(err);
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.visibilityChange !== undefined) {
      document.removeEventListener(this.visibilityChange, this.handleVisibilityChange);
    }

    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("resize", this.handleResize);

    document.removeEventListener("dragenter", this.handleDragEnter);
    document.removeEventListener("dragover", this.handleDragOver);
    document.removeEventListener("drop", this.handleDrop);
    document.removeEventListener("dragleave", this.handleDragLeave);
  }

  setRef = (c) => {
    this.node = c;
  };

  handleHotkeyNew = (e) => {
    e.preventDefault();

    const element = this.node.querySelector(".autosuggest-textarea__textarea");

    if (element) {
      element.focus();
    }
  };

  handleHotkeySearch = (e) => {
    e.preventDefault();

    const element = this.node.querySelector(".search__input");

    if (element) {
      element.focus();
    }
  };

  handleHotkeyForceNew = (e) => {
    this.handleHotkeyNew(e);
    this.props.dispatch(resetCompose());
  };

  handleHotkeyToggleComposeSpoilers = (e) => {
    e.preventDefault();
    this.props.dispatch(changeComposeSpoilerness());
  };

  handleHotkeyFocusColumn = (e) => {
    focusColumn(e.key * 1);
  };

  handleHotkeyLoadMore = () => {
    document.querySelector(".load-more")?.focus();
  };

  handleMoveToTop = () => {
    focusFirstItem();
  };

  handleMoveUp = () => {
    const currentItemIndex = getFocusedItemIndex();
    if (currentItemIndex === -1) {
      return focusColumn(getFocusedColumnIndex());
    } else {
      return focusItemSibling(currentItemIndex, -1);
    }
  };

  handleMoveDown = () => {
    const currentItemIndex = getFocusedItemIndex();
    if (currentItemIndex === -1) {
      return focusColumn(getFocusedColumnIndex());
    } else {
      return focusItemSibling(currentItemIndex, 1);
    }
  };

  handleHotkeyBack = (e) => {
    e.preventDefault();

    const { history } = this.props;

    if (history.location?.state?.fromMastodon) {
      history.goBack();
    } else {
      history.push("/");
    }
  };

  handleHotkeyToggleHelp = () => {
    if (this.props.location.pathname === "/keyboard-shortcuts") {
      this.props.history.goBack();
    } else {
      this.props.history.push("/keyboard-shortcuts");
    }
  };

  handleHotkeyGoToHome = () => {
    this.props.history.push("/home");
  };

  handleHotkeyGoToExplore = () => {
    this.props.history.push("/explore");
  };

  handleHotkeyGoToNotifications = () => {
    this.props.history.push("/notifications");
  };

  handleHotkeyGoToLocal = () => {
    this.props.history.push("/public/local");
  };

  handleHotkeyGoToFederated = () => {
    this.props.history.push("/public");
  };

  handleHotkeyGoToDirect = () => {
    this.props.history.push("/conversations");
  };

  handleHotkeyGoToStart = () => {
    this.props.history.push("/getting-started");
    // Set focus to the navigation after a timeout
    // to allow for it to be displayed first
    setTimeout(() => {
      const navbarSkipTarget = document.querySelector(`#${getNavigationSkipLinkId()}`);
      navbarSkipTarget?.focus();
    }, 0);
  };

  handleHotkeyGoToFavourites = () => {
    this.props.history.push("/favourites");
  };

  handleHotkeyGoToPinned = () => {
    this.props.history.push("/pinned");
  };

  handleHotkeyGoToProfile = () => {
    this.props.history.push(`/@${this.props.username}`);
  };

  handleHotkeyGoToBlocked = () => {
    this.props.history.push("/blocks");
  };

  handleHotkeyGoToMuted = () => {
    this.props.history.push("/mutes");
  };

  handleHotkeyGoToRequests = () => {
    this.props.history.push("/follow_requests");
  };

  render() {
    const { draggingOver } = this.state;
    const { children, isWide, location, layout, moved, firstLaunch, newAccount } = this.props;

    const className = classNames("ui", {
      wide: isWide,
      "fullwidth-columns": this.props.fullWidthColumns,
      "system-font": this.props.systemFontUi,
      "hicolor-privacy-icons": this.props.hicolorPrivacyIcons,
    });

    const handlers = {
      help: this.handleHotkeyToggleHelp,
      new: this.handleHotkeyNew,
      search: this.handleHotkeySearch,
      forceNew: this.handleHotkeyForceNew,
      toggleComposeSpoilers: this.handleHotkeyToggleComposeSpoilers,
      focusColumn: this.handleHotkeyFocusColumn,
      focusLoadMore: this.handleHotkeyLoadMore,
      moveDown: this.handleMoveDown,
      moveUp: this.handleMoveUp,
      moveToTop: this.handleMoveToTop,
      back: this.handleHotkeyBack,
      goToHome: this.handleHotkeyGoToHome,
      goToExplore: this.handleHotkeyGoToExplore,
      goToNotifications: this.handleHotkeyGoToNotifications,
      goToLocal: this.handleHotkeyGoToLocal,
      goToFederated: this.handleHotkeyGoToFederated,
      goToDirect: this.handleHotkeyGoToDirect,
      goToStart: this.handleHotkeyGoToStart,
      goToFavourites: this.handleHotkeyGoToFavourites,
      goToPinned: this.handleHotkeyGoToPinned,
      goToProfile: this.handleHotkeyGoToProfile,
      goToBlocked: this.handleHotkeyGoToBlocked,
      goToMuted: this.handleHotkeyGoToMuted,
      goToRequests: this.handleHotkeyGoToRequests,
      cheat: this.handleDonate,
    };

    const minimalShell =
      !this.props.identity.signedIn &&
      landingPage === "overview" &&
      location.pathname.startsWith("/overview");

    return (
      <Hotkeys global handlers={handlers}>
        <div className={className} ref={this.setRef}>
          {!minimalShell && (
            <SkipLinks
              multiColumn={layout === "multi-column"}
              onFocusGettingStartedColumn={this.handleHotkeyGoToStart}
            />
          )}

          {moved && (
            <div className="flash-message alert">
              <FormattedMessage
                id="moved_to_warning"
                defaultMessage="This account is marked as moved to {moved_to_link}, and may thus not accept new follows."
                values={{
                  moved_to_link: (
                    <Permalink href={moved.get("url")} to={`/@${moved.get("acct")}`}>
                      @{moved.get("acct")}
                    </Permalink>
                  ),
                }}
              />
            </div>
          )}

          <SwitchingColumnsArea
            identity={this.props.identity}
            location={location}
            singleColumn={layout === "mobile" || layout === "single-column"}
            layout={layout}
            forceOnboarding={firstLaunch && newAccount}
          >
            {children}
          </SwitchingColumnsArea>

          {!minimalShell && <NavigationBar />}
          {!minimalShell && <NavPanelFab />}
          {!minimalShell && <InstallAppBanner />}
          <ServiceWorkerUpdateNotice />
          {layout !== "mobile" && <PictureInPicture />}
          <AlertsController />
          {!disableHoverCards && <HoverCardController />}
          <HashtagMenuController />
          <LoadingBarContainer className="loading-bar" />
          <ModalContainer />
          <UploadArea active={draggingOver} onClose={this.closeUploadModal} />
        </div>
      </Hotkeys>
    );
  }
}

export default connect(mapStateToProps)(injectIntl(withRouter(withIdentity(UI))));
